import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './auth.context';

interface Document {
  id: string;
  file_path: string;
  created_at: string;
  metadata: {
    filename?: string;
    display_name?: string;
    file_type?: string;
    chunk_count?: number;
    [key: string]: any;
  };
}

interface QueryResponse {
  answer: string;
  sources: string[];
}

interface RAGDocumentsContextType {
  documents: Document[];
  filteredDocuments: Document[];
  selectedDocument: Document | null;
  queryText: string;
  queryResponse: QueryResponse | null;
  searchTerm: string;
  isLoadingDocuments: boolean;
  isLoadingQuery: boolean;
  error: string | null;
  fetchDocuments: () => Promise<void>;
  selectDocument: (document: Document) => void;
  setQueryText: (text: string) => void;
  setSearchTerm: (term: string) => void;
  submitQuery: () => Promise<void>;
  clearQuery: () => void;
  clearError: () => void;
}

const RAGDocumentsContext = createContext<RAGDocumentsContextType | undefined>(undefined);

export const RAGDocumentsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getToken } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [queryText, setQueryText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [queryResponse, setQueryResponse] = useState<QueryResponse | null>(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isLoadingQuery, setIsLoadingQuery] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = async () => {
    try {
      setIsLoadingDocuments(true);
      setError(null);

      const token = getToken();
      if (!token) {
        setError('Error: Not authenticated');
        return;
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL}/ai/v1/rag/documents`, {
        headers: requestHeaders
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: Document[] = await response.json();
      setDocuments(data);
      setFilteredDocuments(data);
    } catch (error) {
      setError('Error: Failed to fetch documents');
      console.error('Document fetch error:', error);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const selectDocument = (document: Document) => {
    setSelectedDocument(document);
    setQueryResponse(null);
    setQueryText('');
  };

  const submitQuery = async () => {
    if (!selectedDocument) {
      setError('Please select a document first');
      return;
    }

    if (!queryText.trim()) {
      setError('Please enter a query');
      return;
    }

    try {
      setIsLoadingQuery(true);
      setError(null);

      const token = getToken();
      if (!token) {
        setError('Error: Not authenticated');
        return;
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL}/ai/v1/rag/query`, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify({
          question: queryText,
          document_id: selectedDocument.id,
          top_k: 3
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: QueryResponse = await response.json();
      setQueryResponse(data);
    } catch (error) {
      setError('Error: Failed to process query');
      console.error('Query error:', error);
    } finally {
      setIsLoadingQuery(false);
    }
  };

  const clearQuery = () => {
    setQueryText('');
    setQueryResponse(null);
  };

  const clearError = () => {
    setError(null);
  };

  // Filter documents based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredDocuments(documents);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = documents.filter((doc) => {
        const displayName = doc.metadata?.display_name || '';
        const filename = doc.metadata?.filename || doc.file_path.split('/').pop() || '';
        const fileType = doc.metadata?.file_type || '';
        return (
          displayName.toLowerCase().includes(term) ||
          filename.toLowerCase().includes(term) ||
          fileType.toLowerCase().includes(term) ||
          doc.id.toLowerCase().includes(term)
        );
      });
      setFilteredDocuments(filtered);
    }
  }, [searchTerm, documents]);

  const value = {
    documents,
    filteredDocuments,
    selectedDocument,
    queryText,
    queryResponse,
    searchTerm,
    isLoadingDocuments,
    isLoadingQuery,
    error,
    fetchDocuments,
    selectDocument,
    setQueryText,
    setSearchTerm,
    submitQuery,
    clearQuery,
    clearError
  };

  return (
    <RAGDocumentsContext.Provider value={value}>
      {children}
    </RAGDocumentsContext.Provider>
  );
};

export const useRAGDocuments = () => {
  const context = useContext(RAGDocumentsContext);
  if (context === undefined) {
    throw new Error('useRAGDocuments must be used within a RAGDocumentsProvider');
  }
  return context;
};